import { useEffect, useState } from "react";
import {
  gscGetSettings, gscSaveSettings, gscAnalytics,
  gscListSitemaps, gscSubmitSitemap,
  gscRequestIndexing, gscIndexingLog, gscIndexingRetry,
} from "../../lib/api";
import {
  Settings, Search, Map, Send, ShieldCheck, AlertCircle, Loader2,
  CheckCircle2, ExternalLink, Eye, EyeOff, Copy, RefreshCw, Sparkles, RotateCw,
} from "lucide-react";

const TABS = [
  { id: "setup", label: "Setup", icon: Settings },
  { id: "performance", label: "Search Performance", icon: Search },
  { id: "sitemaps", label: "Sitemaps", icon: Map },
  { id: "indexing", label: "Indexing", icon: Send },
];

const SETUP_GUIDE_URL = "https://github.com/your-repo/blob/main/memory/gsc_setup_guide.md";

export default function SeoTools() {
  const [tab, setTab] = useState("setup");
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    gscGetSettings().then(setSettings).catch(() => setSettings({ configured: false }));
  }, []);

  return (
    <div data-testid="seo-tools-page">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-display font-bold text-forest">SEO Tools</h1>
        <p className="text-charcoal-soft text-sm mt-1 max-w-2xl">
          Google Search Console + Indexing API. Wire it up once, then track which pages
          are pulling traffic, submit sitemaps, and request priority indexing.
        </p>
      </div>

      {settings && !settings.configured && tab !== "setup" && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-2xl p-4 mb-4 flex items-start gap-3" data-testid="gsc-not-configured-banner">
          <AlertCircle className="w-5 h-5 text-yellow-700 shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-900">
            <strong>Setup not complete.</strong> Open the <button onClick={() => setTab("setup")} className="underline font-semibold">Setup tab</button> to paste your Google service-account JSON.
          </div>
        </div>
      )}

      <div className="flex gap-1 mb-5 overflow-x-auto pb-1 border-b border-black/10">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition shrink-0 ${
                tab === t.id ? "border-forest text-forest bg-forest/5" : "border-transparent text-charcoal-soft hover:text-forest"
              }`}
              data-testid={`gsc-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "setup" && <SetupTab settings={settings} setSettings={setSettings} />}
      {tab === "performance" && <PerformanceTab settings={settings} />}
      {tab === "sitemaps" && <SitemapsTab settings={settings} />}
      {tab === "indexing" && <IndexingTab settings={settings} />}
    </div>
  );
}

function SetupTab({ settings, setSettings }) {
  const [form, setForm] = useState({ service_account_json: "", site_url: "", verification_tag: "" });
  const [showJson, setShowJson] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const loadingSettings = settings === null;

  useEffect(() => {
    if (settings) {
      setForm((f) => ({
        ...f,
        site_url: settings.site_url || "https://ohioaccidentaid.com/",
        verification_tag: settings.verification_tag || "",
      }));
    }
  }, [settings]);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      // Only send service_account_json if user typed something — otherwise leave the stored one untouched.
      const payload = {
        site_url: form.site_url,
        verification_tag: form.verification_tag,
      };
      if (form.service_account_json.trim()) {
        payload.service_account_json = form.service_account_json;
      }
      const next = await gscSaveSettings(payload);
      setSettings(next);
      setForm((f) => ({ ...f, service_account_json: "" }));
      setMsg({ ok: true, text: "Settings saved." });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="gsc-setup-tab">
      {loadingSettings && (
        <div className="bg-bone-alt rounded-2xl p-4 flex items-center gap-3 text-sm text-charcoal-soft" data-testid="gsc-setup-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading current settings…
        </div>
      )}
      <div className={`rounded-2xl border p-4 flex items-start gap-3 ${settings?.configured ? "bg-emerald-50 border-emerald-200" : "bg-bone-alt border-black/5"}`} data-testid="gsc-status-card">
        {settings?.configured ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
        ) : (
          <AlertCircle className="w-5 h-5 text-charcoal-soft shrink-0 mt-0.5" />
        )}
        <div className="flex-1 text-sm">
          <div className="font-display font-bold text-forest">
            {settings?.configured ? "Search Console is configured" : "Search Console not configured yet"}
          </div>
          {settings?.configured && (
            <div className="text-charcoal-soft mt-1 break-all">
              Service account: <code className="bg-white px-1.5 py-0.5 rounded text-xs">{settings.service_account_email}</code>
              <br />
              Site: <code className="bg-white px-1.5 py-0.5 rounded text-xs">{settings.site_url}</code>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-black/5 p-5">
        <h3 className="font-display font-bold text-forest mb-3">Step-by-step Setup</h3>
        <ol className="list-decimal pl-5 space-y-1.5 text-sm text-charcoal-soft">
          <li>Create a Google Cloud project at <a href="https://console.cloud.google.com/projectcreate" target="_blank" rel="noreferrer" className="text-forest underline">console.cloud.google.com</a>.</li>
          <li>Enable two APIs: <a href="https://console.cloud.google.com/apis/library/searchconsole.googleapis.com" target="_blank" rel="noreferrer" className="text-forest underline">Search Console API</a> and <a href="https://console.cloud.google.com/apis/library/indexing.googleapis.com" target="_blank" rel="noreferrer" className="text-forest underline">Indexing API</a>.</li>
          <li>Create a service account → download a JSON key.</li>
          <li>Verify <code>https://ohioaccidentaid.com/</code> in <a href="https://search.google.com/search-console/welcome" target="_blank" rel="noreferrer" className="text-forest underline">Search Console</a> using the HTML meta-tag method — copy just the <code>content="..."</code> value.</li>
          <li>In Search Console → Settings → Users and permissions, add the service-account email as an <strong>Owner</strong>.</li>
          <li>Paste the JSON + the verification content below, then <strong>Save</strong>. Return to Search Console and click <strong>Verify</strong>.</li>
        </ol>
        <p className="text-xs text-charcoal-soft mt-3">
          Full guide with screenshots: <code>/app/memory/gsc_setup_guide.md</code>
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-black/5 p-5 space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-wider font-bold text-charcoal-soft mb-1.5">
            Site URL (must match Search Console exactly)
          </label>
          <input
            type="text"
            value={form.site_url}
            onChange={(e) => setForm({ ...form, site_url: e.target.value })}
            placeholder="https://ohioaccidentaid.com/"
            className="w-full p-3 rounded-lg border border-black/10 outline-none focus:border-forest text-sm font-mono"
            data-testid="gsc-site-url-input"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs uppercase tracking-wider font-bold text-charcoal-soft">
              Service-account JSON {settings?.configured && "(leave blank to keep current)"}
            </label>
            <button type="button" onClick={() => setShowJson((s) => !s)} className="text-xs text-charcoal-soft hover:text-forest flex items-center gap-1" data-testid="gsc-toggle-json">
              {showJson ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {showJson ? "Hide" : "Show"}
            </button>
          </div>
          <textarea
            value={form.service_account_json}
            onChange={(e) => setForm({ ...form, service_account_json: e.target.value })}
            placeholder={settings?.configured ? "(JSON already saved — paste here only to replace it)" : 'Paste the entire JSON file contents here, starting with {"type": "service_account", ...}'}
            rows={showJson ? 14 : 6}
            className="w-full p-3 rounded-lg border border-black/10 outline-none focus:border-forest text-xs font-mono"
            style={!showJson && form.service_account_json ? { color: "transparent", textShadow: "0 0 8px rgba(0,0,0,0.6)", caretColor: "#0e3a32" } : {}}
            data-testid="gsc-json-input"
          />
          <p className="text-xs text-charcoal-soft mt-1">
            Stored encrypted in MongoDB. Never exposed to the frontend after saving.
          </p>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider font-bold text-charcoal-soft mb-1.5">
            Verification meta-tag content
          </label>
          <input
            type="text"
            value={form.verification_tag}
            onChange={(e) => setForm({ ...form, verification_tag: e.target.value })}
            placeholder='Paste either the content value (e.g. "abc123") OR the whole <meta name="google-site-verification" content="abc123" />'
            className="w-full p-3 rounded-lg border border-black/10 outline-none focus:border-forest text-sm font-mono"
            data-testid="gsc-verification-input"
          />
          <p className="text-xs text-charcoal-soft mt-1">
            Once saved, this is injected into every page's <code>&lt;head&gt;</code> so Search Console can verify ownership.
          </p>
        </div>

        {msg && (
          <div className={`text-sm p-3 rounded-lg ${msg.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`} data-testid="gsc-save-message">
            {msg.text}
          </div>
        )}

        <button onClick={save} disabled={saving} className="btn-primary" data-testid="gsc-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          Save Settings
        </button>
      </div>
    </div>
  );
}

function PerformanceTab({ settings }) {
  const [dimension, setDimension] = useState("page");
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    if (!settings?.configured) return;
    setLoading(true);
    setErr(null);
    try {
      const d = await gscAnalytics(dimension, days, 100);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dimension, days, settings?.configured]); // eslint-disable-line

  const totalClicks = data ? data.rows.reduce((a, r) => a + r.clicks, 0) : 0;
  const totalImpr = data ? data.rows.reduce((a, r) => a + r.impressions, 0) : 0;
  const avgCtr = totalImpr > 0 ? (totalClicks / totalImpr) * 100 : 0;
  const avgPos = data && data.rows.length ? (data.rows.reduce((a, r) => a + r.position, 0) / data.rows.length) : 0;

  return (
    <div className="space-y-4" data-testid="gsc-performance-tab">
      <div className="flex flex-wrap gap-2 items-center bg-white p-3 rounded-xl border border-black/5">
        <div className="flex gap-1 bg-bone-alt rounded-lg p-1">
          {["page", "query"].map((d) => (
            <button
              key={d}
              onClick={() => setDimension(d)}
              className={`text-xs px-3 py-1.5 rounded font-semibold transition ${dimension === d ? "bg-forest text-bone" : "text-charcoal hover:text-forest"}`}
              data-testid={`gsc-dim-${d}`}
            >
              {d === "page" ? "Top Pages" : "Top Queries"}
            </button>
          ))}
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="text-sm p-2 rounded-lg border border-black/10 bg-white"
          data-testid="gsc-range"
        >
          <option value={7}>Last 7 days</option>
          <option value={28}>Last 28 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <button onClick={load} className="btn-ghost text-xs px-3 py-2 ml-auto" data-testid="gsc-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      {err && <div className="text-sm bg-red-50 text-red-800 p-3 rounded-lg" data-testid="gsc-perf-error">{err}</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Clicks" value={totalClicks.toLocaleString()} />
            <Kpi label="Impressions" value={totalImpr.toLocaleString()} />
            <Kpi label="CTR" value={`${avgCtr.toFixed(2)}%`} />
            <Kpi label="Avg Position" value={avgPos.toFixed(1)} />
          </div>
          <div className="text-xs text-charcoal-soft">
            {data.start_date} → {data.end_date} (data has ~2 day lag from Google)
          </div>
          <div className="bg-white rounded-2xl border border-black/5 overflow-hidden" data-testid="gsc-perf-table">
            <div className="grid grid-cols-12 text-[10px] uppercase tracking-wider font-bold text-charcoal-soft bg-bone-alt p-3 border-b border-black/5">
              <div className="col-span-6">{dimension === "page" ? "Page" : "Query"}</div>
              <div className="col-span-2 text-right">Clicks</div>
              <div className="col-span-2 text-right">Impr.</div>
              <div className="col-span-1 text-right">CTR</div>
              <div className="col-span-1 text-right">Pos</div>
            </div>
            {data.rows.length === 0 ? (
              <div className="p-6 text-center text-charcoal-soft text-sm">No data yet for this range.</div>
            ) : (
              data.rows.map((r, i) => (
                <div key={i} className="grid grid-cols-12 text-sm p-3 border-b border-black/5 last:border-0 items-center">
                  <div className="col-span-6 truncate font-mono text-xs">
                    {dimension === "page" ? (
                      <a href={r.key} target="_blank" rel="noreferrer" className="text-forest hover:underline">
                        {r.key}
                      </a>
                    ) : r.key}
                  </div>
                  <div className="col-span-2 text-right font-semibold">{Math.round(r.clicks)}</div>
                  <div className="col-span-2 text-right">{Math.round(r.impressions)}</div>
                  <div className="col-span-1 text-right">{(r.ctr * 100).toFixed(1)}%</div>
                  <div className="col-span-1 text-right">{r.position.toFixed(1)}</div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-black/5 p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-charcoal-soft">{label}</div>
      <div className="font-display font-black text-2xl text-forest mt-1">{value}</div>
    </div>
  );
}

function SitemapsTab({ settings }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitUrl, setSubmitUrl] = useState("");
  const [msg, setMsg] = useState(null);

  const load = async () => {
    if (!settings?.configured) return;
    setErr(null);
    try {
      const d = await gscListSitemaps();
      setData(d);
      setSubmitUrl((u) => u || d.auto_sitemap_url || "");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load sitemaps");
    }
  };
  useEffect(() => { load(); }, [settings?.configured]); // eslint-disable-line

  const submit = async () => {
    setSubmitting(true);
    setMsg(null);
    try {
      await gscSubmitSitemap(submitUrl);
      setMsg({ ok: true, text: `Submitted ${submitUrl}` });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Submission failed" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="gsc-sitemaps-tab">
      {err && <div className="text-sm bg-red-50 text-red-800 p-3 rounded-lg">{err}</div>}
      <div className="bg-white rounded-2xl border border-black/5 p-5">
        <h3 className="font-display font-bold text-forest mb-2">Submit a sitemap</h3>
        <p className="text-xs text-charcoal-soft mb-3">
          Your live sitemap is auto-generated at <code>{data?.auto_sitemap_url || "/sitemap.xml"}</code>. Submit it once after launch, and re-submit after big content drops.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={submitUrl}
            onChange={(e) => setSubmitUrl(e.target.value)}
            className="flex-1 p-2.5 rounded-lg border border-black/10 outline-none focus:border-forest text-sm font-mono"
            placeholder="https://ohioaccidentaid.com/sitemap.xml"
            data-testid="sitemap-url-input"
          />
          <button onClick={submit} disabled={submitting || !submitUrl} className="btn-secondary text-sm" data-testid="sitemap-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Submit
          </button>
        </div>
        {msg && (
          <div className={`text-sm mt-3 p-3 rounded-lg ${msg.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>
            {msg.text}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        <div className="p-4 border-b border-black/5 flex items-center justify-between">
          <h3 className="font-display font-bold text-forest">Submitted sitemaps</h3>
          <button onClick={load} className="btn-ghost text-xs px-3 py-1.5"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
        {!data ? (
          <div className="p-6 text-center text-charcoal-soft text-sm">Loading…</div>
        ) : data.sitemaps.length === 0 ? (
          <div className="p-6 text-center text-charcoal-soft text-sm">No sitemaps registered with Search Console yet. Submit one above.</div>
        ) : (
          data.sitemaps.map((s, i) => (
            <div key={i} className="p-4 border-b border-black/5 last:border-0 text-sm" data-testid={`sitemap-row-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <a href={s.path} target="_blank" rel="noreferrer" className="font-mono text-xs text-forest hover:underline flex items-center gap-1 break-all">
                  {s.path} <ExternalLink className="w-3 h-3 shrink-0" />
                </a>
              </div>
              <div className="text-xs text-charcoal-soft">
                Type: {s.type || "—"} · Last submitted: {s.last_submitted ? new Date(s.last_submitted).toLocaleString() : "—"}
                {s.is_pending ? " · Pending" : ""}
                {Number(s.errors) > 0 ? <span className="text-red-700"> · {s.errors} error(s)</span> : ""}
                {Number(s.warnings) > 0 ? <span className="text-yellow-700"> · {s.warnings} warning(s)</span> : ""}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function IndexingTab({ settings }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [log, setLog] = useState([]);
  const [retryingId, setRetryingId] = useState(null);

  const loadLog = async () => {
    try {
      const r = await gscIndexingLog();
      setLog(r.rows || []);
    } catch (e) { /* not configured yet */ }
  };
  useEffect(() => { loadLog(); }, [settings?.configured]); // eslint-disable-line

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await gscRequestIndexing(url);
      setMsg({ ok: true, text: `Notified Google: ${url}` });
      setUrl("");
      loadLog();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Indexing request failed" });
    } finally {
      setBusy(false);
    }
  };

  const retry = async (logId) => {
    setRetryingId(logId);
    setMsg(null);
    try {
      await gscIndexingRetry(logId);
      setMsg({ ok: true, text: "Re-submitted to Google." });
      loadLog();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Retry failed" });
    } finally {
      setRetryingId(null);
    }
  };

  const statusLabel = (r) => {
    if (r.status_code === 200) return { text: "OK", style: "bg-emerald-100 text-emerald-800" };
    if (r.status_code === 0) return { text: "Skipped — GSC not configured", style: "bg-yellow-100 text-yellow-800" };
    if (r.status_code === -1) return { text: "Error", style: "bg-red-100 text-red-800" };
    return { text: `HTTP ${r.status_code}`, style: "bg-red-100 text-red-800" };
  };

  return (
    <div className="space-y-4" data-testid="gsc-indexing-tab">
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <strong>Daily quota: ~200 requests.</strong> Posts auto-notify Google whenever their status flips to <em>Published</em>.
          Failed or skipped attempts appear below with a <strong>Resubmit</strong> button so you can retry once GSC is configured.
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-black/5 p-5">
        <h3 className="font-display font-bold text-forest mb-2">Manual indexing request</h3>
        <div className="flex gap-2">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://ohioaccidentaid.com/blog/your-new-post"
            className="flex-1 p-2.5 rounded-lg border border-black/10 outline-none focus:border-forest text-sm font-mono"
            data-testid="indexing-url-input"
          />
          <button onClick={submit} disabled={busy || !url} className="btn-primary text-sm" data-testid="indexing-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Notify Google
          </button>
        </div>
        {msg && (
          <div className={`text-sm mt-3 p-3 rounded-lg ${msg.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>
            {msg.text}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        <div className="p-4 border-b border-black/5 flex items-center justify-between">
          <h3 className="font-display font-bold text-forest">Indexing log</h3>
          <button onClick={loadLog} className="btn-ghost text-xs px-3 py-1.5"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
        {log.length === 0 ? (
          <div className="p-6 text-center text-charcoal-soft text-sm">No indexing activity yet.</div>
        ) : (
          log.map((r) => {
            const s = statusLabel(r);
            const canRetry = r.status_code !== 200;
            return (
              <div key={r.id} className="p-3 border-b border-black/5 last:border-0 text-xs" data-testid={`indexing-log-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ${s.style}`}>{s.text}</span>
                  <span className="text-[10px] uppercase tracking-wider text-charcoal-soft">
                    {r.trigger === "auto" ? "Auto" : r.trigger === "manual-retry" ? "Retry" : "Manual"}
                    {r.source ? ` · ${r.source}` : ""}
                  </span>
                  <span className="font-mono break-all flex-1 min-w-[200px]">{r.url}</span>
                  <span className="text-charcoal-soft shrink-0">{new Date(r.created_at).toLocaleString()}</span>
                  {canRetry && (
                    <button
                      onClick={() => retry(r.id)}
                      disabled={retryingId === r.id || !settings?.configured}
                      className="btn-ghost text-[11px] px-2 py-1 disabled:opacity-40"
                      data-testid={`indexing-retry-${r.id}`}
                      title={settings?.configured ? "Resubmit this URL to Google" : "Configure GSC in the Setup tab first"}
                    >
                      {retryingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
                      Resubmit
                    </button>
                  )}
                </div>
                {r.response && r.status_code !== 200 && (
                  <div className="text-charcoal-soft mt-1 pl-1 break-all">
                    {typeof r.response === "string" ? r.response : JSON.stringify(r.response).slice(0, 200)}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
